import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import {
  PlaceAutocompleteService,
  PlaceDetails,
  PlaceSuggestion,
} from "app/shared/google-maps/place-autocomplete.service";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";

@Component({
  selector: "app-place-autocomplete",
  templateUrl: "./place-autocomplete.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AutoComplete, FormsModule, AutocompleteEmptyMessageComponent],
})
export class PlaceAutocompleteComponent {
  private placeAutocompleteService = inject(PlaceAutocompleteService);

  public readonly value = input("");
  public readonly placeholder = input("");
  public readonly disabled = input(false);
  public readonly dataCy = input("");

  public readonly valueChange = output<string>();
  public readonly placeSelect = output<PlaceDetails>();

  // The picked place's coordinates and address arrive one round trip after the
  // pick, so callers need to know not to save the leg yet.
  public readonly pendingChange = output<boolean>();

  private readonly autocomplete = viewChild(AutoComplete);

  // The text belongs to the parent, but typing has to show immediately — some
  // callers deliberately do not write it back until a place is picked.
  protected readonly query = linkedSignal(() => this.value());
  protected readonly suggestions = signal<PlaceSuggestion[]>([]);
  protected readonly loading = signal(false);

  // Predictions come back out of order often enough to matter: a slow answer
  // to "warsa" must not overwrite the list for "warsaw".
  private lastRequest = 0;

  constructor() {
    this.placeAutocompleteService.preload();

    // The control owns its input element and forwards neither `data-cy` nor
    // the typing-correction attributes, so they are set on it directly.
    afterNextRender(() => {
      const input = this.autocomplete()?.inputEL()?.nativeElement;
      if (!input) {
        return;
      }

      input.setAttribute("data-cy", this.dataCy());
      input.setAttribute("autocorrect", "off");
      input.setAttribute("autocapitalize", "off");
      input.setAttribute("spellcheck", "false");
    });
  }

  protected async search(event: AutoCompleteCompleteEvent): Promise<void> {
    const request = ++this.lastRequest;

    this.suggestions.set([]);
    this.loading.set(true);

    try {
      const suggestions = await this.placeAutocompleteService.fetchSuggestions(
        event.query,
      );

      if (request === this.lastRequest) {
        this.suggestions.set(suggestions);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (request === this.lastRequest) {
        this.loading.set(false);
      }
    }
  }

  protected async select(event: AutoCompleteSelectEvent): Promise<void> {
    const suggestion = event.value as PlaceSuggestion;

    this.setQuery(suggestion.label);
    this.pendingChange.emit(true);

    try {
      this.placeSelect.emit(
        await this.placeAutocompleteService.fetchDetails(suggestion),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingChange.emit(false);
    }
  }

  // The control writes the picked suggestion object into the model before the
  // label replaces it, so anything that is not text is dropped here. An
  // emptied input and the clear button both arrive as null, not "", and have
  // to reach the caller as an empty text or it keeps the stale place.
  protected onQueryChange(value: string | PlaceSuggestion | null): void {
    if (value === null || value === undefined) {
      this.setQuery("");
      return;
    }

    if (typeof value === "string") {
      this.setQuery(value);
    }
  }

  private setQuery(value: string): void {
    this.query.set(value);
    this.valueChange.emit(value);
  }
}
